import React, { useRef, useState, useEffect } from 'react';
import { Button } from 'antd';
import { dermatologyMarkTypes } from './dermatologyUtils';

import baseImage from './assets/dermatology-base.png';

const DermatologyImage = ({ marks, onMarksChange, disabled, setDermatologyChartRef, fromExternal }) => {
  const [selectedType, setSelectedType] = useState('L');
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (!setDermatologyChartRef) return;

    const captureImage = () =>
      new Promise((resolve, reject) => {
        const imageElement = imageRef.current;
        if (!imageElement) return reject(new Error('Image element not mounted'));

        const canvas = document.createElement('canvas');
        canvas.width = imageElement.naturalWidth || imageElement.offsetWidth;
        canvas.height = imageElement.naturalHeight || imageElement.offsetHeight;
        
        const context = canvas.getContext('2d');
        context.drawImage(imageElement, 0, 0, canvas.width, canvas.height);

        const markSize = Math.max(18, canvas.width * 0.03);
        const radius = 4;

        context.font = `bold ${markSize * 0.5}px Arial`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';

        marks.forEach((mark) => {
          const xPos = (parseFloat(mark.x) / 100) * canvas.width;
          const yPos = (parseFloat(mark.y) / 100) * canvas.height;
          const halfSize = markSize / 2;

          context.fillStyle = dermatologyMarkTypes[mark.type]?.color || '#B43E23';
          
          context.beginPath();
          if (context.roundRect) {
            context.roundRect(xPos - halfSize, yPos - halfSize, markSize, markSize, radius);
          } else {
            context.rect(xPos - halfSize, yPos - halfSize, markSize, markSize);
          }
          context.fill();

          context.fillStyle = '#FFFFFF';
          context.fillText(mark.type, xPos, yPos);
        });

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Canvas conversion to blob failed'));
            resolve(new File([blob], 'imagen-facial.png', { type: 'image/png' }));
          },
          'image/png'
        );
      });

    setDermatologyChartRef(captureImage);
  }, [marks, setDermatologyChartRef]);

  const handleImageClick = (event) => {
    if (disabled) return;

    const imageRectangle = containerRef.current.getBoundingClientRect();
    const xPercentage = ((event.clientX - imageRectangle.left) / imageRectangle.width) * 100;
    const yPercentage = ((event.clientY - imageRectangle.top) / imageRectangle.height) * 100;

    onMarksChange([
      ...marks,
      {
        id: Date.now(),
        type: selectedType,
        x: xPercentage.toFixed(2),
        y: yPercentage.toFixed(2),
      },
    ]);
  };

  const undoLastMark = () => {
    if (marks.length === 0) return;
    onMarksChange(marks.slice(0, -1));
  };

  return (
    <div className="dermatology-interactive-section">
      <div className="dermatology-controls">
        <div className={`dermatology-controls-mark-row ${fromExternal ? 'compact-layout' : ''}`}>
          <span className="dermatology-control-label">Tipo de marca:</span>
          
          <div className="dermatology-controls-row-wrapper">
            <div className={`dermatology-controls-buttons ${fromExternal ? 'compact-layout' : ''}`}>
              {Object.entries(dermatologyMarkTypes).map(([key, value]) => (
                <Button
                  key={key}
                  size={fromExternal ? 'small' : 'middle'}
                  className={`dermatology-type-button ${selectedType === key ? 'active' : ''}`}
                  type={selectedType === key ? 'primary' : 'default'}
                  onClick={() => setSelectedType(key)}
                  style={selectedType === key ? { backgroundColor: value.color, borderColor: value.color } : {}}
                >
                  {key} = {value.label}
                </Button>
              ))}
            </div>

            <Button
              type="link"
              danger
              onClick={undoLastMark}
              disabled={marks.length === 0 || disabled}
              className={`dermatology-undo-button ${fromExternal ? 'compact-layout' : ''}`}
            >
              Quitar última marca
            </Button>
          </div>
        </div>
      </div>

      <div
        className="dermatology-image-wrapper"
        onClick={handleImageClick}
        ref={containerRef}
      >
        <img
          ref={imageRef}
          src={baseImage}
          alt="Anatomía Facial"
          className="dermatology-base-image"
          draggable={false}
          crossOrigin="anonymous"
        />
        {marks.map((mark) => (
          <div
            key={mark.id}
            className={`dermatology-mark dermatology-mark-${mark.type}`}
            style={{
              left: `${mark.x}%`,
              top: `${mark.y}%`,
            }}
          >
            {mark.type}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DermatologyImage;
